
import { Component, Ref, Watch, Vue } from 'vue-property-decorator';
import { AxiosError, AxiosResponse } from 'axios';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import zxcvbn from 'zxcvbn';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import Registration from '../models/registration';

Vue.use(VueReCaptcha, {
  siteKey: '6Lci1PsfAAAAACL0IrjV91ofbgfBEW9itIsBzpH_',
  loaderOptions: {
    autoHideBadge: true,
  },
});

class RegistrationStatus {
  isHackazonUser = true;
  isSpaceRaceUser = true;
  isRegistered = true;
}

import vueCountryRegionSelect from 'vue-country-region-select';
Vue.use(vueCountryRegionSelect);

function isValidHackazonEmail(value: string) {
  return value.match(/^[A-Za-z0-9.+_-]+@[A-Za-z0-9._-]+\.[a-zA-Z]+$/);
}

function isValidHackazonUsername(value: string) {
  return value.match(/^[a-zA-Z0-9][A-Za-z0-9_:]{2,100}$/);
}

import { extend } from 'vee-validate';

extend('password', {
  params: ['target'],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate(value, { target }: Record<string, any>) {
    return value === target;
  },
  message: 'Password confirmation does not match',
});

extend('passwordStrength', {
  validate(value) {
    const result: zxcvbn.ZXCVBNResult = zxcvbn(value);
    if (result.feedback.warning) {
      return (
        result.feedback.warning + '. (' + result.feedback.suggestions[0] + ')'
      );
    }

    if (result.score < 3) {
      return 'This password is not strong enough';
    }

    return true;
  },
});

extend('hackazonEmail', {
  validate(value) {
    if (isValidHackazonEmail(value)) {
      return true;
    }
    return 'The {_field_} field must be a valid email';
  },
});

extend('hackazonUsername', {
  validate(value) {
    if (isValidHackazonUsername(value)) {
      return true;
    }

    return 'The {_field_} field is invalid';
  },
});

@Component({
  components: {
    ValidationProvider,
    ValidationObserver,
  },
})
export default class RegistrationForm extends Vue {
  @Ref('observer') readonly observer!: InstanceType<typeof ValidationObserver>;

  registrationModel = new Registration({
    country: this.$route.params.country?.toUpperCase(),
  });

  maxTeamMembers = 5;
  isSending = false;
  emailAddressLookupDone = false;
  firstLoadDone = false;
  isLoading = false;
  confirmed = false;
  cancelTokenSource = this.$http.CancelToken.source();

  occupationOptions = [
    {
      value: null,
      text: 'My occupation is...',
      disabled: true,
    },
    {
      value: 'Student',
      text: 'Student',
    },
    {
      value: 'Professional',
      text: 'Professional',
    },
  ];

  @Watch('registrationModel.email')
  checkEmail() {
    if (isValidHackazonEmail(this.registrationModel.email)) {
      this.emailAddressLookupDone = false;
      this.isLoading = true;

      this.cancelTokenSource.cancel();
      this.cancelTokenSource = this.$http.CancelToken.source();

      this.$http
        .post('/api/registration/user_status', this.registrationModel, {
          cancelToken: this.cancelTokenSource.token,
        })
        .then((response: AxiosResponse<RegistrationStatus>) => {
          this.registrationModel.isHackazonUser = response.data.isHackazonUser;
          this.registrationModel.isSpaceRaceUser = response.data.isSpaceRaceUser;
          this.registrationModel.isRegistered = response.data.isRegistered;
          this.firstLoadDone = true;
          this.emailAddressLookupDone = true;
        })
        .catch((error: AxiosError) => {
          if (!this.$http.isCancel(error)) {
            alert(error.message);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    } else {
      this.firstLoadDone = false;
    }
  }

  async onSubmit() {
    this.isSending = true;

    await this.$recaptchaLoaded();
    this.registrationModel.captchaToken = await this.$recaptcha('register');

    this.$http
      .post(
        this.registrationModel.isHackazonUser
          ? '/api/user/joingroup'
          : '/api/registration',
        this.registrationModel
      )
      .then(() => {
        this.confirmed = true;
      })
      .catch((error: AxiosError) => {
        if (error.response) {
          this.observer.setErrors(error.response.data);
        } else {
          alert(error.message);
        }

        this.isSending = false;
      });
  }
}
